/**
 * Media file-pick capability (src/platform/).
 *
 * Contract: every capability has a working web implementation; native
 * branches (via isNativePlatform()) are added in #981 and MUST keep the
 * web fallback. Web path: a hidden <input type="file">; #981 replaces
 * this with the camera-roll picker plugin.
 */

export interface PickMediaOptions {
  accept?: string
  multiple?: boolean
  capture?: 'user' | 'environment'
}

/** Open the host file picker and resolve the chosen files ([] on cancel). */
export function pickMediaFiles(options: PickMediaOptions = {}): Promise<File[]> {
  return new Promise((resolve) => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = options.accept ?? 'image/*,video/*'
    input.multiple = options.multiple ?? false
    // setAttribute, not `input.capture = …`: the `capture` IDL attribute is not
    // reflected by every engine (WebKit among them), so the assignment lands as
    // a plain JS property, the content attribute is never set, and the camera
    // hint is silently dropped on the one platform it exists for.
    if (options.capture) input.setAttribute('capture', options.capture)
    input.style.display = 'none'

    const finish = (files: File[]) => {
      input.remove()
      resolve(files)
    }

    input.addEventListener('change', () => finish(Array.from(input.files ?? [])))
    input.addEventListener('cancel', () => finish([]))

    document.body.appendChild(input)
    input.click()
  })
}
